"use client";

import type { MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	ArtistIcon,
	DSRadius,
	EmptyState,
	GlassButton,
	GlassCard,
	NavigationBar,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import { invokeArtistRoute } from "@/infrastructure/supabase/functions";
import { useTranslation, type TranslationKey } from "@/i18n/ArtistI18n";


type Phase =
	| "idle"
	| "requesting"
	| "listening"
	| "matching"
	| "matched"
	| "no-match"
	| "error";

interface Match {
	readonly title: string;
	readonly artist: string;
	readonly artworkUrl?: string;
	readonly externalUrl?: string;
}

interface HistoryItem {
	readonly id: string;
	readonly title: string;
	readonly artist: string;
	readonly artworkUrl?: string;
	readonly externalUrl?: string;
	readonly recognizedAt: number;
}

const HISTORY_STORAGE_KEY = "artist.shazam.history.v1";
const MAX_RECORDING_MS = 9_000;
const MAX_HISTORY = 50;

export function ShazamPage(): ReactNode {
	const { t } = useTranslation();
	const [phase, setPhase] = useState<Phase>("idle");
	const [match, setMatch] = useState<Match | null>(null);
	const [statusMessage, setStatusMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [history, setHistory] = useState<readonly HistoryItem[]>([]);
	const [menuIndex, setMenuIndex] = useState<number | null>(null);

	const recorderRef = useRef<MediaRecorder | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const chunksRef = useRef<BlobPart[]>([]);
	const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const menuDialogRef = useRef<HTMLDialogElement | null>(null);
	const requestIdRef = useRef(0);

	useEffect(() => {
		try {
			const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
			if (!raw) return;
			const parsed = JSON.parse(raw) as unknown;
			if (Array.isArray(parsed)) {
				setHistory(parsed.filter(isHistoryItem));
			}
		} catch {
		}
	}, []);

	useEffect(() => {
		try {
			window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
		} catch {
		}
	}, [history]);

	const stopTracks = useCallback((): void => {
		if (stopTimerRef.current) {
			clearTimeout(stopTimerRef.current);
			stopTimerRef.current = null;
		}
		streamRef.current?.getTracks().forEach((track) => {
			track.stop();
		});
		streamRef.current = null;
		recorderRef.current = null;
	}, []);

	useEffect(() => {
		return () => {
			stopTracks();
		};
	}, [stopTracks]);

	useEffect(() => {
		const dialog = menuDialogRef.current;
		if (dialog === null) return;
		if (menuIndex !== null && !dialog.open) {
			dialog.showModal();
		} else if (menuIndex === null && dialog.open) {
			dialog.close();
		}
	}, [menuIndex]);

	const identify = useCallback(
		async (audio: Blob): Promise<void> => {
			setPhase("matching");
			setStatusMessage(t("recognition.status.matching"));
			try {
				const form = new FormData();
				form.append("audio", audio, "recognition.webm");
				const result = await invokeArtistRoute<{
					outcome?: "match" | "no-match";
					match?: Match;
					message?: string;
				}>("/recognition", {
					body: form,
					timeoutMilliseconds: 20_000,
				});
				if (result.outcome === "match" && result.match) {
					const m = result.match;
					setMatch(m);
					setPhase("matched");
					setStatusMessage(null);
					setHistory((prev) =>
						[toHistoryItem(m), ...prev].slice(0, MAX_HISTORY),
					);
				} else {
					setPhase("no-match");
					setMatch(null);
					setStatusMessage(t("recognition.status.noMatch"));
				}
			} catch {
				setPhase("error");
				setMatch(null);
				setStatusMessage(null);
				setErrorMessage(t("recognition.error.generic"));
			}
		},
		[t],
	);

	const begin = useCallback(async (): Promise<void> => {
		setErrorMessage(null);
		setMatch(null);
		setStatusMessage(t("recognition.requesting"));
		setPhase("requesting");
		const currentRequestId = ++requestIdRef.current;
		try {
			if (
				typeof navigator === "undefined" ||
				!navigator.mediaDevices?.getUserMedia
			) {
				throw new Error("unsupported");
			}
			if (typeof MediaRecorder === "undefined") throw new Error("unsupported");
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					channelCount: 1,
					echoCancellation: false,
					noiseSuppression: false,
				},
				video: false,
			});
			if (currentRequestId !== requestIdRef.current) {
				stream.getTracks().forEach((track) => {
					track.stop();
				});
				return;
			}
			streamRef.current = stream;
			const chunks: BlobPart[] = [];
			chunksRef.current = chunks;
			const recorder = new MediaRecorder(stream);
			recorderRef.current = recorder;
			recorder.ondataavailable = (event) => {
				if (event.data.size > 0) chunks.push(event.data);
			};
			recorder.onstop = () => {
				const audio = new Blob(chunks, {
					type: recorder.mimeType || "audio/webm",
				});
				stopTracks();
				if (currentRequestId !== requestIdRef.current) return;
				void identify(audio);
			};
			recorder.start(250);
			setPhase("listening");
			setStatusMessage(t("recognition.subheading.listening"));
			stopTimerRef.current = setTimeout(() => {
				if (recorderRef.current?.state === "recording") {
					recorderRef.current.stop();
				}
			}, MAX_RECORDING_MS);
		} catch (error) {
			stopTracks();
			if (currentRequestId !== requestIdRef.current) return;
			setPhase("error");
			setMatch(null);
			setStatusMessage(null);
			if (error instanceof DOMException && error.name === "NotAllowedError") {
				setErrorMessage(t("recognition.error.micDenied"));
			} else if (error instanceof Error && error.message === "unsupported") {
				setErrorMessage(t("recognition.error.unsupported"));
			} else {
				setErrorMessage(t("recognition.error.micStart"));
			}
		}
	}, [identify, stopTracks, t]);

	const cancel = useCallback((): void => {
		++requestIdRef.current;
		if (recorderRef.current?.state === "recording") {
			recorderRef.current.onstop = null;
			try {
				recorderRef.current.stop();
			} catch {
			}
		}
		stopTracks();
		setPhase("idle");
		setStatusMessage(null);
		setMatch(null);
	}, [stopTracks]);

	const finish = useCallback((): void => {
		if (recorderRef.current?.state === "recording") {
			recorderRef.current.stop();
		}
	}, []);

	const retry = useCallback((): void => {
		setErrorMessage(null);
		setMatch(null);
		setStatusMessage(null);
		void begin();
	}, [begin]);

	const clearHistory = useCallback((): void => {
		setHistory([]);
	}, []);

	const removeFromHistory = useCallback((index: number): void => {
		setHistory((prev) => prev.filter((_, i) => i !== index));
	}, []);

	const handleOpenMenu = useCallback((index: number): void => {
		setMenuIndex(index);
	}, []);

	const closeMenu = useCallback((): void => {
		setMenuIndex(null);
	}, []);

	const activeMenuItem = menuIndex !== null ? history[menuIndex] : undefined;

	const handleMenuSearch = useCallback((): void => {
		if (activeMenuItem === undefined) return;
		const q = encodeURIComponent(
			`${activeMenuItem.title} ${activeMenuItem.artist}`,
		);
		window.location.href = `/search?q=${q}`;
		setMenuIndex(null);
	}, [activeMenuItem]);

	const handleMenuOpenExternal = useCallback((): void => {
		if (
			activeMenuItem === undefined ||
			activeMenuItem.externalUrl === undefined
		)
			return;
		window.open(activeMenuItem.externalUrl, "_blank", "noopener,noreferrer");
		setMenuIndex(null);
	}, [activeMenuItem]);

	const handleMenuDelete = useCallback((): void => {
		if (menuIndex === null) return;
		removeFromHistory(menuIndex);
		setMenuIndex(null);
	}, [menuIndex, removeFromHistory]);

	const isBusy = phase === "requesting" || phase === "matching";
	const isListening = phase === "listening";

	let hero: ReactNode;
	if (phase === "matched" && match) {
		hero = <MatchResultHero match={match} onRetry={retry} />;
	} else if (phase === "error") {
		hero = (
			<EmptyState
				symbol={<ArtistIcon name="info" size={38} />}
				title={t("recognition.error.generic")}
				message={errorMessage ?? t("recognition.error.generic")}
				actionLabel={t("common.retry")}
				actionSymbol={<ArtistIcon name="sparkle" size={16} />}
				onAction={retry}
			/>
		);
	} else {
		hero = (
			<RecognitionHero
				phase={phase}
				statusMessage={statusMessage}
				isBusy={isBusy}
				isListening={isListening}
				onPrimary={isListening ? finish : begin}
				onCancel={cancel}
			/>
		);
	}

	const toastMessage =
		statusMessage !== null && phase !== "matched" ? statusMessage : null;

	return (
		<section
			className="native-page recognition-shazam-page"
			aria-label={t("recognition.title")}
		>
			<NavigationBar title={t("recognition.title")} backHref="/" />
			{toastMessage !== null ? (
				<Toast style={phase === "error" ? "error" : "info"}>
					{toastMessage}
				</Toast>
			) : null}
			<div className="recognition-shazam-hero">{hero}</div>
			{history.length > 0 ? (
				<HistoryList
					history={history}
					onOpenMenu={handleOpenMenu}
					onClear={clearHistory}
				/>
			) : null}
			<dialog
				ref={menuDialogRef}
				className="recognition-shazam-menu"
				onClose={() => setMenuIndex(null)}
			>
				<div className="recognition-shazam-menu__header">
					{activeMenuItem === undefined ? null : (
						<>
							<strong>{activeMenuItem.title}</strong>
							<span>{activeMenuItem.artist}</span>
						</>
					)}
				</div>
				<button
					type="button"
					className="recognition-shazam-menu__option"
					onClick={handleMenuSearch}
				>
					<ArtistIcon name="search" size={18} />
					<span>{t("recognition.history.menu.search")}</span>
				</button>
				<button
					type="button"
					className="recognition-shazam-menu__option"
					onClick={handleMenuOpenExternal}
					disabled={activeMenuItem?.externalUrl === undefined}
				>
					<ArtistIcon name="link" size={18} />
					<span>{t("recognition.history.menu.openExternal")}</span>
				</button>
				<button
					type="button"
					className="recognition-shazam-menu__option recognition-shazam-menu__option--danger"
					onClick={handleMenuDelete}
				>
					<ArtistIcon name="trash" size={18} />
					<span>{t("recognition.history.menu.delete")}</span>
				</button>
				<button
					type="button"
					className="recognition-shazam-menu__cancel"
					onClick={closeMenu}
				>
					{t("recognition.history.menu.close")}
				</button>
			</dialog>
		</section>
	);
}

function RecognitionHero({
	phase,
	statusMessage,
	isBusy,
	isListening,
	onPrimary,
	onCancel,
}: Readonly<{
	phase: Phase;
	statusMessage: string | null;
	isBusy: boolean;
	isListening: boolean;
	onPrimary: () => void;
	onCancel: () => void;
}>): ReactNode {
	const { t } = useTranslation();
	const orbActive = isListening || isBusy;
	const heading =
		phase === "requesting"
			? t("recognition.requesting")
			: isListening
				? t("recognition.listening")
				: phase === "matching"
					? t("recognition.matching")
					: phase === "no-match"
						? t("recognition.noMatch")
						: t("recognition.idle");
	const subheading =
		statusMessage !== null
			? statusMessage
			: isListening
				? t("recognition.subheading.listening")
				: t("recognition.subheading.idle");
	const primaryLabel = isListening
		? t("recognition.action.stop")
		: phase === "requesting"
			? t("recognition.action.requesting")
			: phase === "matching"
				? t("recognition.action.recognizing")
				: t("recognition.action.start");
	const primarySymbol = isListening ? (
		<ArtistIcon name="pause" size={16} />
	) : (
		<ArtistIcon name="waveform" size={16} />
	);
	return (
		<div className="recognition-shazam-recognition">
			<button
				type="button"
				className={`recognition-shazam-orb${orbActive ? " recognition-shazam-orb--active" : ""}`}
				onClick={onPrimary}
				disabled={isBusy}
				aria-label={primaryLabel}
			>
				<span className="recognition-shazam-orb__ripple" aria-hidden="true" />
				<span
					className="recognition-shazam-orb__ripple recognition-shazam-orb__ripple--secondary"
					aria-hidden="true"
				/>
				<span className="recognition-shazam-orb__glyph">
					<ArtistIcon name="waveform" size={72} strokeWidth={1.5} />
				</span>
			</button>
			<div className="recognition-shazam-summary">
				<h2>{heading}</h2>
				<p>{subheading}</p>
			</div>
			<div className="recognition-shazam-actions">
				<GlassButton
					kind="primary"
					size="lg"
					expands
					symbol={primarySymbol}
					onClick={onPrimary}
					disabled={isBusy}
				>
					{primaryLabel}
				</GlassButton>
				{isListening ? (
					<GlassButton
						kind="secondary"
						size="lg"
						expands
						symbol={<ArtistIcon name="close" size={16} />}
						onClick={onCancel}
					>
						{t("common.cancel")}
					</GlassButton>
				) : null}
			</div>
		</div>
	);
}

function MatchResultHero({
	match,
	onRetry,
}: Readonly<{ match: Match; onRetry: () => void }>): ReactNode {
	const { t } = useTranslation();
	const externalUrl = match.externalUrl;
	const searchHref = `/search?q=${encodeURIComponent(
		`${match.title} ${match.artist}`,
	)}`;
	return (
		<GlassCard className="recognition-shazam-match" cornerRadius={DSRadius.xl}>
			<div className="recognition-shazam-match__art">
				{match.artworkUrl !== undefined ? (
				// biome-ignore lint/performance/noImgElement: shazam match artwork with dynamic styling; next/image not feasible on static export
				<img
					src={match.artworkUrl}
					alt={`${match.title} ${t("recognition.title")}`}
				/>
			) : (
				<ArtistIcon name="music" size={48} />
			)}
			</div>
			<div className="recognition-shazam-match__meta">
				<h2 className="recognition-shazam-match__title">{match.title}</h2>
				<p className="recognition-shazam-match__artist">{match.artist}</p>
			</div>
			<div className="recognition-shazam-match__actions">
				{externalUrl !== undefined ? (
					<GlassButton
						kind="primary"
						size="lg"
						expands
						symbol={<ArtistIcon name="play" size={16} />}
						onClick={() =>
							window.open(externalUrl, "_blank", "noopener,noreferrer")
						}
					>
						{t("recognition.match.openInAppleMusic")}
					</GlassButton>
				) : null}
				<GlassButton
					kind="secondary"
					size="lg"
					expands
					symbol={<ArtistIcon name="search" size={16} />}
					onClick={() => {
						window.location.href = searchHref;
					}}
				>
					{t("recognition.match.searchInArtist")}
				</GlassButton>
				<GlassButton
					kind="secondary"
					size="lg"
					expands
					symbol={<ArtistIcon name="waveform" size={16} />}
					onClick={onRetry}
				>
					{t("recognition.action.retry")}
				</GlassButton>
			</div>
		</GlassCard>
	);
}

function HistoryList({
	history,
	onOpenMenu,
	onClear,
}: Readonly<{
	history: readonly HistoryItem[];
	onOpenMenu: (index: number) => void;
	onClear: () => void;
}>): ReactNode {
	const { t } = useTranslation();
	const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelLongPress = useCallback(() => {
		if (longPressTimer.current) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	}, []);

	const startLongPress = useCallback(
		(index: number) => {
			longPressTimer.current = setTimeout(() => {
				onOpenMenu(index);
			}, 500);
		},
		[onOpenMenu],
	);

	useEffect(() => cancelLongPress, [cancelLongPress]);

	return (
		<GlassCard
			className="recognition-shazam-history"
			cornerRadius={DSRadius.xl}
			style={{ padding: 0 }}
		>
			<div className="recognition-shazam-history__head">
				<h3>{t("recognition.history.title")}</h3>
				<button
					type="button"
					className="recognition-shazam-history__clear"
					onClick={onClear}
				>
					{t("recognition.history.clear")}
				</button>
			</div>
			<ul className="recognition-shazam-history__list">
				{history.map((item, index) => (
					<li
						key={item.id}
						className="recognition-shazam-history__item"
						onContextMenu={(event: MouseEvent<HTMLLIElement>) => {
							event.preventDefault();
							onOpenMenu(index);
						}}
						onTouchStart={() => startLongPress(index)}
						onTouchEnd={cancelLongPress}
						onTouchMove={cancelLongPress}
					>
						<div className="recognition-shazam-history__thumb">
							{item.artworkUrl !== undefined ? (
							// biome-ignore lint/performance/noImgElement: shazam history thumbnail; next/image not feasible on static export
							<img src={item.artworkUrl} alt="" />
						) : (
							<ArtistIcon name="music" size={20} />
						)}
						</div>
						<div className="recognition-shazam-history__meta">
							<span className="recognition-shazam-history__title">
								{item.title}
							</span>
							<span className="recognition-shazam-history__artist">
								{item.artist}
							</span>
						</div>
						<span className="recognition-shazam-history__time">
							{formatRelativeTime(item.recognizedAt, t)}
						</span>
						<button
							type="button"
							className="recognition-shazam-history__more"
							aria-label={t("recognition.history.menu.title")}
							onClick={() => onOpenMenu(index)}
						>
							<ArtistIcon name="more" size={18} />
						</button>
					</li>
				))}
			</ul>
		</GlassCard>
	);
}

function toHistoryItem(m: Match): HistoryItem {
	return {
		id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
		title: m.title,
		artist: m.artist,
		...(m.artworkUrl !== undefined ? { artworkUrl: m.artworkUrl } : {}),
		...(m.externalUrl !== undefined ? { externalUrl: m.externalUrl } : {}),
		recognizedAt: Date.now(),
	};
}

function isHistoryItem(value: unknown): value is HistoryItem {
	if (typeof value !== "object" || value === null) return false;
	const v = value as Record<string, unknown>;
	return (
		typeof v.id === "string" &&
		typeof v.title === "string" &&
		typeof v.artist === "string" &&
		typeof v.recognizedAt === "number"
	);
}

function formatRelativeTime(
	epochMs: number,
	t: (key: TranslationKey, params?: Record<string, string | number>) => string,
): string {
	const diffMs = Date.now() - epochMs;
	const diffMin = Math.floor(diffMs / 60_000);
	if (diffMin < 1) return t("recognition.time.justNow");
	if (diffMin < 60)
		return t("recognition.time.minutesAgo", { minutes: diffMin });
	const diffHr = Math.floor(diffMin / 60);
	if (diffHr < 24) return t("recognition.time.hoursAgo", { hours: diffHr });
	const diffDay = Math.floor(diffHr / 24);
	if (diffDay < 7) return t("recognition.time.daysAgo", { days: diffDay });
	return new Date(epochMs).toLocaleDateString("zh-CN");
}
